import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import {
  AlertCircle,
  Bell,
  CheckCircle,
  Copy,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  Globe,
  Key,
  Lock,
  Mail,
  Save,
  Shield,
  User,
  Zap,
} from 'lucide-react'
import { useId, useState } from 'react'
import { publisherApi } from '../lib/api'

interface ProfileData {
  id: string
  email: string
  username: string
  fullName?: string
  company?: string
  website?: string
  phone?: string
  country?: string
  timezone?: string
  language?: string
  status: 'active' | 'inactive' | 'suspended'
  tier: 'starter' | 'pro' | 'enterprise'
  emailVerified: boolean
  twoFactorEnabled: boolean
  apiKey?: string
  webhookUrl?: string
  notificationPreferences: {
    emailAlerts: boolean
    paymentNotifications: boolean
    performanceReports: boolean
    newsletterUpdates: boolean
  }
  createdAt: string
  lastLoginAt: string
}

export function Profile() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'general' | 'security' | 'notifications' | 'api'>(
    'general',
  )
  const [showApiKey, setShowApiKey] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<Partial<ProfileData>>({})
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  // Generate unique IDs for form fields
  const fullNameId = useId()
  const usernameId = useId()
  const emailId = useId()
  const phoneId = useId()
  const companyId = useId()
  const websiteId = useId()
  const countryId = useId()
  const timezoneId = useId()
  const _languageId = useId()
  const currentPasswordId = useId()
  const newPasswordId = useId()
  const confirmPasswordId = useId()
  const webhookUrlId = useId()

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: publisherApi.getProfile,
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<ProfileData>) => publisherApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      setIsEditing(false)
    },
  })

  const updatePasswordMutation = useMutation({
    mutationFn: (data: typeof passwordData) => publisherApi.updatePassword(data),
    onSuccess: () => {
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    },
  })

  const regenerateApiKeyMutation = useMutation({
    mutationFn: () => publisherApi.regenerateApiKey(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })

  if (isLoading || !profile) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="space-y-4">
              {[...Array(5)].map(() => (
                <div
                  key={Math.random().toString(36).substring(7)}
                  className="h-10 bg-gray-200 rounded"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleUpdateProfile = () => {
    updateProfileMutation.mutate(formData)
  }

  const handleUpdatePassword = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Passwords do not match')
      return
    }
    if (passwordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters')
      return
    }
    updatePasswordMutation.mutate(passwordData)
  }

  const copyApiKey = () => {
    if (profile.apiKey) {
      navigator.clipboard.writeText(profile.apiKey)
    }
  }

  const tierBadgeColors = {
    starter: 'bg-gray-100 text-gray-800',
    pro: 'bg-blue-100 text-blue-800',
    enterprise: 'bg-purple-100 text-purple-800',
  }

  const statusBadgeColors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-yellow-100 text-yellow-800',
    suspended: 'bg-red-100 text-red-800',
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Profile Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-white">
                {profile.username?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {profile.fullName || profile.username}
              </h2>
              <p className="text-gray-600">{profile.email}</p>
              <div className="flex items-center space-x-3 mt-2">
                <span
                  className={clsx(
                    'px-2 py-1 text-xs font-medium rounded-full',
                    tierBadgeColors[profile.tier],
                  )}
                >
                  {profile.tier.charAt(0).toUpperCase() + profile.tier.slice(1)} Plan
                </span>
                <span
                  className={clsx(
                    'px-2 py-1 text-xs font-medium rounded-full',
                    statusBadgeColors[profile.status],
                  )}
                >
                  {profile.status.charAt(0).toUpperCase() + profile.status.slice(1)}
                </span>
                {profile.emailVerified && (
                  <span className="flex items-center text-xs text-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Member since</p>
            <p className="text-sm font-medium text-gray-900">
              {format(new Date(profile.createdAt), 'MMM d, yyyy')}
            </p>
            <p className="text-sm text-gray-500 mt-2">Last login</p>
            <p className="text-sm font-medium text-gray-900">
              {format(new Date(profile.lastLoginAt), 'MMM d, yyyy h:mm a')}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              type="button"
              onClick={() => setActiveTab('general')}
              className={clsx(
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'general'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700',
              )}
            >
              <User className="w-4 h-4 inline-block mr-2" />
              General
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('security')}
              className={clsx(
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'security'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700',
              )}
            >
              <Lock className="w-4 h-4 inline-block mr-2" />
              Security
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notifications')}
              className={clsx(
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'notifications'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700',
              )}
            >
              <Bell className="w-4 h-4 inline-block mr-2" />
              Notifications
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('api')}
              className={clsx(
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'api'
                  ? 'text-indigo-600 border-indigo-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700',
              )}
            >
              <Key className="w-4 h-4 inline-block mr-2" />
              API & Webhooks
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor={fullNameId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name
                  </label>
                  <input
                    id={fullNameId}
                    type="text"
                    value={isEditing ? formData.fullName || '' : profile.fullName || ''}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label
                    htmlFor={usernameId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Username
                  </label>
                  <input
                    id={usernameId}
                    type="text"
                    value={profile.username}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div>
                  <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id={emailId}
                    type="email"
                    value={profile.email}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div>
                  <label htmlFor={phoneId} className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    id={phoneId}
                    type="tel"
                    value={isEditing ? formData.phone || '' : profile.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={!isEditing}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label
                    htmlFor={companyId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Company
                  </label>
                  <input
                    id={companyId}
                    type="text"
                    value={isEditing ? formData.company || '' : profile.company || ''}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label
                    htmlFor={websiteId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Website
                  </label>
                  <input
                    id={websiteId}
                    type="url"
                    value={isEditing ? formData.website || '' : profile.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    disabled={!isEditing}
                    placeholder="https://example.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  />
                </div>
                <div>
                  <label
                    htmlFor={countryId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Country
                  </label>
                  <select
                    id={countryId}
                    value={isEditing ? formData.country || '' : profile.country || ''}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  >
                    <option value="">Select Country</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="AU">Australia</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="JP">Japan</option>
                    <option value="IN">India</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor={timezoneId}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Timezone
                  </label>
                  <select
                    id={timezoneId}
                    value={isEditing ? formData.timezone || '' : profile.timezone || ''}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50"
                  >
                    <option value="">Select Timezone</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Paris">Paris (CET)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEDT)</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                {isEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false)
                        setFormData({})
                      }}
                      className="px-4 py-2 text-gray-600 hover:text-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleUpdateProfile}
                      disabled={updateProfileMutation.isPending}
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(true)
                      setFormData(profile)
                    }}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    Edit Profile
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Security Recommendation</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Enable two-factor authentication to add an extra layer of security to your
                      account.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
                <div className="space-y-4 max-w-md">
                  <div>
                    <label
                      htmlFor={currentPasswordId}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Current Password
                    </label>
                    <input
                      id={currentPasswordId}
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, currentPassword: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={newPasswordId}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      New Password
                    </label>
                    <input
                      id={newPasswordId}
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, newPassword: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={confirmPasswordId}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Confirm New Password
                    </label>
                    <input
                      id={confirmPasswordId}
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) =>
                        setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUpdatePassword}
                    disabled={updatePasswordMutation.isPending}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {updatePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Two-Factor Authentication
                </h3>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-gray-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Two-Factor Authentication</p>
                      <p className="text-sm text-gray-600">
                        {profile.twoFactorEnabled ? 'Enabled' : 'Not enabled'}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={clsx(
                      'px-4 py-2 rounded-lg text-sm font-medium',
                      profile.twoFactorEnabled
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200',
                    )}
                  >
                    {profile.twoFactorEnabled ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Email Notifications</h3>
              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-gray-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email Alerts</p>
                      <p className="text-sm text-gray-600">
                        Receive alerts for important account activities
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={profile.notificationPreferences?.emailAlerts ?? true}
                    onChange={() => {}}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    <CreditCard className="w-5 h-5 text-gray-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Payment Notifications</p>
                      <p className="text-sm text-gray-600">
                        Get notified about payments and payouts
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={profile.notificationPreferences?.paymentNotifications ?? true}
                    onChange={() => {}}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    <Zap className="w-5 h-5 text-gray-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Performance Reports</p>
                      <p className="text-sm text-gray-600">
                        Weekly and monthly performance summaries
                      </p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={profile.notificationPreferences?.performanceReports ?? false}
                    onChange={() => {}}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </label>
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-gray-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Newsletter & Updates</p>
                      <p className="text-sm text-gray-600">Product updates and industry news</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={profile.notificationPreferences?.newsletterUpdates ?? false}
                    onChange={() => {}}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                </label>
              </div>
            </div>
          )}

          {/* API Tab */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">API Key</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Use this API key to authenticate requests to our API endpoints.
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={profile.apiKey || 'pk_live_xxxxxxxxxxxxxxxxxxxxxxxxxx'}
                        readOnly
                        className="w-full px-4 py-2 pr-20 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          {showApiKey ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={copyApiKey}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => regenerateApiKeyMutation.mutate()}
                      disabled={regenerateApiKeyMutation.isPending}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:bg-gray-200 disabled:text-gray-500"
                    >
                      {regenerateApiKeyMutation.isPending ? 'Regenerating...' : 'Regenerate'}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Webhook URL</h3>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor={webhookUrlId}
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Endpoint URL
                    </label>
                    <input
                      id={webhookUrlId}
                      type="url"
                      value={profile.webhookUrl || ''}
                      placeholder="https://your-domain.com/webhook"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      We'll send POST requests to this URL for conversion events
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Save Webhook
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Test Webhook
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">API Documentation</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Learn how to integrate with our API and automate your workflow.
                </p>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  View Documentation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
